import type {
	CacheConfig,
	Variables,
	RequestParameters,
	GraphQLResponse,
	GraphQLSingularResponse,
	FetchFunction,
	INetwork,
} from 'relay-runtime';
import { Network, Observable } from 'relay-runtime';

import {
	setNetworkStatus,
	ClientNetworkStatus,
} from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache.ts';
import { fg } from '@confluence/feature-gating';

import { BatchFetcher, getDefaultBatchingConfig } from './batchFetcher.ts';
import { shouldTerminateQueriesOnExternalPage } from './externalShare.ts';
import { isIncrementalRequest } from './incrementalResponse.ts';
import {
	readDataFromStream,
	writeDataToStream,
	initializeSSR,
	SSR_NAMESPACE,
} from './streaming.ts';
import { getRelayWebSocketSubscriptionHandler } from './RelayWebSocketSubscriptionHandlerFactory.ts';
import { composeMiddlewares } from './middlewareComposer.ts';
import { getMiddlewareConfig } from './middlewareConfig.ts';

// Lazily created so fg() is not evaluated at module load time (no-module-level-eval).
// The killswitch gate is re-evaluated on every fetch() call so toggling the gate
// takes effect immediately without requiring a process restart (important for SSR).
let _batchFetcher: BatchFetcher | undefined;
const getBatchFetcher = (): BatchFetcher => {
	if (!_batchFetcher) {
		_batchFetcher = new BatchFetcher(getDefaultBatchingConfig());
	}
	// Re-evaluate killswitch on every call so SSR processes pick up gate changes
	_batchFetcher.setBatchingConfig({
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		enabled: !fg('confluence_relay_batching_killswitch'),
	});
	return _batchFetcher;
};

export const __resetBatchFetcherForTests = (): void => {
	_batchFetcher = undefined;
};

const fetchFn: FetchFunction = (request, variables, cacheConfig) => {
	if (
		shouldTerminateQueriesOnExternalPage({
			operationName: request.name,
			metadata: cacheConfig.metadata,
		})
	) {
		return Observable.create((observer) => {
			observer.complete();
		});
	}

	const result = readDataFromStream(request, variables, cacheConfig);
	if (result) {
		return Observable.create((observer) => {
			observer.next(result);
			observer.complete();
		});
	}

	return Observable.create((observer) => {
		if (isIncrementalRequest(cacheConfig)) {
			const subscription = getBatchFetcher()
				.fetchIncremental(request, variables, cacheConfig)
				.subscribe({
					next: (payload) => observer.next(payload),
					error: (error: Error) => observer.error(error),
					complete: () => observer.complete(),
				});

			return () => {
				subscription.unsubscribe();
			};
		}

		getBatchFetcher()
			.fetch(request, variables, cacheConfig)
			.then(({ data, errors }) => {
				if (data === null && errors !== null) {
					observer.error(errors);
					observer.complete();
					return;
				}

				const response: GraphQLSingularResponse = errors ? { data, errors } : { data };
				if (data) {
					writeDataToStream(request, variables, cacheConfig, response);
				}
				observer.next(response);
				observer.complete();
			})
			.catch((error) => {
				observer.error(error);
			});
	});
};

const subscribeFn = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
): Observable<GraphQLResponse> => {
	const wsHandler = getRelayWebSocketSubscriptionHandler();

	return Observable.create((observer: any) => {
		const unsubscribe = wsHandler.subscribe(request, variables, cacheConfig, {
			next: (value: any) => observer.next(value),
			error: (error: Error) => observer.error(error),
			complete: () => observer.complete(),
		});

		return () => {
			unsubscribe();
		};
	});
};

export const getNetwork = (): INetwork => {
	initializeSSR(SSR_NAMESPACE);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = !!process.env.REACT_SSR;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRTest = process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch';

	// Get middleware configurations
	const middlewareConfig = getMiddlewareConfig({ isSSR, isSSRTest });

	// Compose all enabled middlewares
	const composedFetch = composeMiddlewares(fetchFn, middlewareConfig);

	const network = Network.create(composedFetch, subscribeFn);

	if (!isSSR) {
		setNetworkStatus(ClientNetworkStatus.ONLINE);
	}

	return network;
};

export const getNoopNetwork = (): INetwork => {
	return Network.create((_request, _variables, _cacheConfig) => {
		return Observable.create((observer) => {
			observer.complete();
		});
	});
};
